import React from 'react';
import { Grid, Typography, withStyles, IconButton, TextField, MenuItem, Button, Divider } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import ConditionTable from '../ConditionTable';
import AdvancedSwitch from '../AdvancedSwitch';
import { createBlankRule } from './defaultRules';
import OutlinedSelect from '../../../../controls/OutlinedSelect';
import { checkComplexConditions } from '../../fieldDialogLogic';

const styles = () => ({
    messageVerticalSpacing: {
        marginTop: '2%',
    },
    alignEnd: {
        textAlign: 'end',
    },
});

class ValidationRule extends React.Component {
    handleRuleChange = (event, index) => {
        const tag = event.target.name;
        const value = event.target.value;
        const newRule = {
            ...this.props.ruleInfo,
            [tag]: value,
        };
        this.props.handleRuleChange(index, newRule);
    };

    clearRule = (index) => {
        this.props.handleRuleChange(index, createBlankRule());
    };

    toggleAdvanced = () => {
        if (!this.props.ruleInfo.advancedMode) {
            const jsonRule = {
                conditions: {
                    [this.props.ruleInfo.booleanOperator]: this.props.ruleInfo.conditions,
                },
                event: {
                    type: this.props.ruleInfo.type,
                    params: {
                        message: this.props.ruleInfo.message,
                    },
                },
            };
            const jsonRuleString = JSON.stringify(jsonRule);
            const newRules = {
                ...this.props.ruleInfo,
                advancedMode: !this.props.ruleInfo.advancedMode,
                advancedRules: jsonRuleString,
            };
            this.props.handleRuleChange(this.props.index, newRules);
        } else {
            try {
                let newRule = '';
                if (this.props.ruleInfo.advancedRules === '') {
                    newRule = {
                        ...createBlankRule(),
                        advancedMode: !this.props.ruleInfo.advancedMode,
                    };
                } else {
                    let advancedRules = JSON.parse(this.props.ruleInfo.advancedRules);
                    let nonadvanced = {
                        booleanOperator: advancedRules.conditions.any ? 'any' : 'all',
                        conditions: advancedRules.conditions.any
                            ? advancedRules.conditions.any
                            : advancedRules.conditions.all,
                        message: advancedRules.event.params.message,
                        type: advancedRules.event.type,
                    };
                    newRule = {
                        ...nonadvanced,
                        advancedMode: !this.props.ruleInfo.advancedMode,
                    };
                }
                this.props.handleRuleChange(this.props.index, newRule);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
        }
    };

    handleChangeAdvanced = (event, index) => {
        let advancedRequiredMsg = null;
        try {
            let advancedRules = JSON.parse(event.target.value);
            let conditions = advancedRules.conditions;
            advancedRequiredMsg = checkComplexConditions(conditions)
                ? 'The conditions are too complex to display in basic mode'
                : null;
        } catch (e) {
            if (event.target.value === '') {
                this.clearRule(index);
            } else {
                advancedRequiredMsg = 'The information you have entered is not valid JSON';
            }
        }
        const newRule = {
            ...this.props.ruleInfo,
            advancedRules: event.target.value,
            advancedRequiredMsg: advancedRequiredMsg,
        };
        this.props.handleRuleChange(index, newRule);
    };

    render() {
        const { ruleInfo, index } = this.props;

        return (
            <React.Fragment>
                <Grid container xs={12} justifyContent="space-between">
                    <Grid item xs={6}>
                        <Typography variant="button">{`Rule ${index + 1}`}</Typography>
                    </Grid>
                    <Grid item xs={5} className={this.props.classes.alignEnd}>
                        <AdvancedSwitch
                            checked={ruleInfo.advancedMode}
                            onToggle={this.toggleAdvanced}
                            advancedRequiredMsg={ruleInfo.advancedRequiredMsg}
                        />
                    </Grid>
                    <Grid item xs={1} className={this.props.classes.alignEnd}>
                        <IconButton size="small" aria-label="delete rule" onClick={() => this.props.deleteRule(index)}>
                            <ClearIcon />
                        </IconButton>
                    </Grid>
                </Grid>
                {ruleInfo.advancedMode ? (
                    <Grid item xs={12}>
                        <Typography variant="body1">Enter field rule in JSON format</Typography>
                        <TextField
                            id={`Rule-${index + 1}`}
                            label="Rules"
                            multiline
                            variant="outlined"
                            rows="8"
                            defaultValue={ruleInfo.advancedRules}
                            onChange={(e) => this.handleChangeAdvanced(e, index)}
                            margin="normal"
                            fullWidth
                        />
                    </Grid>
                ) : (
                    <Grid container item spacing={2}>
                        <Grid xs={12} container item alignItems="center" justifyContent="space-between">
                            <Grid container xs={9} item spacing={1} alignItems="center">
                                <Grid item>
                                    <Typography component={'span'}>This field is valid if</Typography>
                                </Grid>
                                <Grid item>
                                    <OutlinedSelect
                                        name="booleanOperator"
                                        fullWidth={false}
                                        margin="dense"
                                        variant="outlined"
                                        id={index.toString()}
                                        value={ruleInfo.booleanOperator}
                                        onChange={(e) => this.handleRuleChange(e, index)}
                                    >
                                        <MenuItem value={'any'}>{'Any'}</MenuItem>
                                        <MenuItem value={'all'}>{'All'}</MenuItem>
                                    </OutlinedSelect>
                                </Grid>
                                <Grid item>
                                    <Typography>of the following conditions are met</Typography>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Button color="primary" onClick={() => this.clearRule(index)}>
                                    Clear All
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                        <ConditionTable
                            conditions={ruleInfo.conditions}
                            entries={this.props.entries}
                            ruleIndex={index}
                            booleanOperator={ruleInfo.booleanOperator}
                            handleRuleChange={this.handleRuleChange}
                        />
                        <Grid item xs={9} className={this.props.classes.messageVerticalSpacing}>
                            <Typography component="label" htmlFor={'message-' + index}>
                                Message
                            </Typography>
                            <TextField
                                variant="outlined"
                                name="message"
                                id={'message-' + index}
                                onChange={(e) => this.handleRuleChange(e, index)}
                                value={ruleInfo.message}
                                multiline
                                margin="dense"
                                rows="2"
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                )}
            </React.Fragment>
        );
    }
}

export default withStyles(styles)(ValidationRule);
